import React, { useState, useEffect, useRef } from "react";

import { getDevicesByRegionApi } from "@/lib/api/device";
import {
  createLocationApi,
  updateLocationApi,
  getLocationDetailApi,
  uploadLocationPhotoApi,
  deleteLocationPhotoApi,
  addDeviceToLocationApi,
  removeDeviceFromLocationApi,
} from "@/lib/api/location";
import { regions, findChildRegions, getRegionByCode } from "@/constants/regions";
import { useModal } from "@/store/modalStore";
import { useLoading } from "@/store/loadingStore";
import "./LocationForm.css";

declare global {
  interface Window {
    daum: any;
    kakao: any;
  }
}

interface PhotoFile {
  file: File | null;
  preview: string;
  id?: number;
}

// 나주시 지역 데이터만 필터링
const najuRegions = regions.filter((region) => region.fullName.includes("나주시"));

// 나주시 읍면동 목록
const najuDistricts = najuRegions
  .filter(
    (region) =>
      region.code.startsWith("4617") && region.code.endsWith("000") && region.code !== "4617000000"
  )
  .sort((a, b) => {
    const order = ["면", "읍", "동"];
    const aType = order.findIndex((type) => a.name.endsWith(type));
    const bType = order.findIndex((type) => b.name.endsWith(type));

    if (aType !== bType) return aType - bType;
    return a.name.localeCompare(b.name);
  });

const LocationFormView = () => {
  const modal = useModal();
  const { startLoading, stopLoading } = useLoading();
  // URL에서 id 파라미터 가져오기
  const urlParams = new URLSearchParams(window.location.search);
  const id = urlParams.get("id") || window.location.pathname.split("/").pop();
  const isEditMode = !!id && id !== "register" && id !== "new";

  const [selectedTab, setSelectedTab] = useState("sensor1");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedDistrictCode, setSelectedDistrictCode] = useState("");
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState({
    sensor1: null,
    sensor2: null,
    sensor3: null,
    sensor4: null,
    sensor5: null,
  });
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [devicePhotos, setDevicePhotos] = useState({
    sensor1: [],
    sensor2: [],
    sensor3: [],
    sensor4: [],
    sensor5: [],
  });
  const [originalDevices, setOriginalDevices] = useState([]); // 원본 기기 데이터 저장
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // 폼 데이터
  const [formData, setFormData] = useState({
    locationName: "",
    locationCode: "",
    locationType: "",
    branch: "",
    businessNumber: "",
    representative: "",
    contact: "",
    establishmentDate: "",
    businessScale: "",
    employeeCount: "",
    province: "전라남도",
    city: "나주시",
    district: "",
    detailAddress: "",
    latitude: "",
    longitude: "",
    notes: "",
    roadAddress: "",
    jibunAddress: "",
    manager: "",
    contact2: "",
    operationDays: {
      mon: false,
      tue: false,
      wed: false,
      thu: false,
      fri: false,
      sat: false,
      sun: false,
    },
    operationHours: "",
    odorInfo: "",
    isPublic: true,
    sensors: {
      sensor1: {
        deviceId: null,
        deviceCode: "",
        deviceName: "",
        latitude: "",
        longitude: "",
        locationDetail: "",
      },
      sensor2: {
        deviceId: null,
        deviceCode: "",
        deviceName: "",
        latitude: "",
        longitude: "",
        locationDetail: "",
      },
      sensor3: {
        deviceId: null,
        deviceCode: "",
        deviceName: "",
        latitude: "",
        longitude: "",
        locationDetail: "",
      },
      sensor4: {
        deviceId: null,
        deviceCode: "",
        deviceName: "",
        latitude: "",
        longitude: "",
        locationDetail: "",
      },
      sensor5: {
        deviceId: null,
        deviceCode: "",
        deviceName: "",
        latitude: "",
        longitude: "",
        locationDetail: "",
      },
    },
  });

  // 사진 상태
  const [photos, setPhotos] = useState<PhotoFile[]>([
    { file: null, preview: "" },
    { file: null, preview: "" },
    { file: null, preview: "" },
  ]);

  // 이미지 팝업 상태
  const [imagePopup, setImagePopup] = useState({
    isOpen: false,
    src: "",
    alt: "",
  });

  // 수정 모드일 때 데이터 로드
  useEffect(() => {
    if (isEditMode && id) {
      loadLocationData(parseInt(id));
    }
  }, [isEditMode, id]);

  // 소재지 데이터 로드
  const loadLocationData = async (locationId) => {
    try {
      startLoading("소재지 정보를 불러오는 중...");
      const response = await getLocationDetailApi(locationId);

      console.log("=== API 응답 전체 ===");
      console.log(response);

      // devices 배열 확인
      if (response.devices) {
        console.log("=== devices 배열 상세 ===");
        console.log("devices 개수:", response.devices.length);
        response.devices.forEach((device, index) => {
          console.log(`Device[${index}]:`, device);
          console.log(`- sensorNumber: ${device.sensorNumber}`);
          console.log(`- device 객체:`, device.device);
          console.log(`- deviceId:`, device.deviceId);
          console.log(`- sensorAlias:`, device.sensorAlias);
        });
      } else {
        console.log("devices 배열이 없습니다.");
      }

      // 폼 데이터 설정
      const newFormData = {
        locationName: response.locationName || "",
        locationCode: response.locationCode || "",
        locationType: response.locationType || "",
        branch: "",
        businessNumber: response.businessNumber || "",
        representative: response.representative || "",
        contact: response.contact || "",
        establishmentDate: response.establishmentDate || "",
        businessScale: response.businessScale || "",
        employeeCount: response.employeeCount?.toString() || "",
        province: response.province || "전라남도",
        city: response.city || "나주시",
        district: response.district || "",
        detailAddress: response.detailAddress || "",
        latitude: response.latitude?.toString() || "",
        longitude: response.longitude?.toString() || "",
        notes: response.notes || "",
        roadAddress: response.fullAddress || "",
        jibunAddress: response.fullAddress || "",
        manager: "",
        contact2: response.email || "",
        operationDays: {
          mon: false,
          tue: false,
          wed: false,
          thu: false,
          fri: false,
          sat: false,
          sun: false,
        },
        operationHours: "",
        odorInfo: response.odorInfo || "",
        isPublic: response.isPublic !== undefined ? response.isPublic : true,
        sensors: {
          sensor1: {
            deviceId: null,
            deviceCode: "",
            deviceName: "",
            latitude: "",
            longitude: "",
            locationDetail: "",
          },
          sensor2: {
            deviceId: null,
            deviceCode: "",
            deviceName: "",
            latitude: "",
            longitude: "",
            locationDetail: "",
          },
          sensor3: {
            deviceId: null,
            deviceCode: "",
            deviceName: "",
            latitude: "",
            longitude: "",
            locationDetail: "",
          },
          sensor4: {
            deviceId: null,
            deviceCode: "",
            deviceName: "",
            latitude: "",
            longitude: "",
            locationDetail: "",
          },
          sensor5: {
            deviceId: null,
            deviceCode: "",
            deviceName: "",
            latitude: "",
            longitude: "",
            locationDetail: "",
          },
        },
      };

      // 센서 정보 설정 전 로그
      console.log("=== 센서 정보 설정 시작 ===");

      if (response.devices && response.devices.length > 0) {
        response.devices.forEach((locationDevice, index) => {
          console.log(`LocationDevice[${index}]:`, locationDevice);
          console.log(`- device 객체:`, locationDevice.device);
          console.log(`- sensorNumber:`, locationDevice.sensorNumber);
        });
      }

      // 원본 기기 데이터 저장 (ACTIVE 상태만)
      const activeDevices = (response.devices || []).filter(device => device.status === 'ACTIVE');
      setOriginalDevices(activeDevices);
      console.log("활성 기기 목록:", activeDevices);

      // 센서 정보 설정 (ACTIVE 상태만)
      if (activeDevices.length > 0) {
        activeDevices.forEach((locationDevice) => {
          const sensorNumber = locationDevice.sensorNumber;
          const sensorKey = `sensor${sensorNumber}`;

          if (newFormData.sensors[sensorKey] && sensorNumber >= 1 && sensorNumber <= 5) {
            const device = locationDevice.device;

            if (device) {
              console.log(`센서 ${sensorNumber} device 정보:`, {
                deviceId: device.id,
                deviceCode: device.deviceId,
                photos: device.photos,
                photosCount: device.photos ? device.photos.length : 0,
              });

              newFormData.sensors[sensorKey] = {
                deviceId: device.id,
                deviceCode: device.deviceId,
                deviceName:
                  locationDevice.sensorAlias ||
                  device.installation?.deviceName ||
                  device.brand ||
                  "",
                latitude: device.installation?.latitude?.toString() || "",
                longitude: device.installation?.longitude?.toString() || "",
                locationDetail:
                  locationDevice.installationPosition || device.installation?.locationDetail || "",
                photos: device.photos || [],
              };
            }
          }
        });
      }

      setFormData(newFormData);

      // 사진 설정
      if (response.photos && response.photos.length > 0) {
        const newPhotos: PhotoFile[] = [...photos]; // 타입 명시
        response.photos.forEach((photo: any, index: number) => {
          if (index < 3) {
            console.log(`사진 ${index}:`, {
              원본: photo,
              filePath: photo.filePath,
              fileUrl: photo.fileUrl,
              변환URL: getImageUrl(photo.filePath),
            });

            newPhotos[index] = {
              file: null,
              preview: photo.fileUrl || getImageUrl(photo.filePath || photo.fileName),
              id: photo.id, // 이제 타입 에러 없음
            };
          }
        });
        setPhotos(newPhotos);
      }

      // 지도 중심 이동
      if (response.latitude && response.longitude && mapInstanceRef.current) {
        const coords = new window.kakao.maps.LatLng(response.latitude, response.longitude);
        mapInstanceRef.current.setCenter(coords);

        // 마커 표시
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }
        const marker = new window.kakao.maps.Marker({
          position: coords,
          map: mapInstanceRef.current,
        });
        markerRef.current = marker;
      }
    } catch (error) {
      console.error("소재지 정보 로드 실패:", error);
      modal.alert("오류", "소재지 정보를 불러오는데 실패했습니다.");
      window.location.href = "/location";
    } finally {
      stopLoading();
    }
  };

  // 카카오맵 초기화
  useEffect(() => {
    const checkAndInitMap = () => {
      if (window.kakao && window.kakao.maps && window.kakao.maps.LatLng) {
        initializeMap();
      } else if (window.kakao && window.kakao.maps && window.kakao.maps.load) {
        window.kakao.maps.load(() => {
          initializeMap();
        });
      } else {
        setTimeout(checkAndInitMap, 100);
      }
    };

    checkAndInitMap();

    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
    };
  }, []);

  useEffect(() => {
    if (formData.latitude && formData.longitude && mapInstanceRef.current) {
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        const coords = new window.kakao.maps.LatLng(lat, lng);

        // 지도 중심 이동
        mapInstanceRef.current.setCenter(coords);

        // 기존 마커 제거
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }

        // 새 마커 생성
        const marker = new window.kakao.maps.Marker({
          position: coords,
          map: mapInstanceRef.current,
        });

        markerRef.current = marker;
      }
    }
  }, [formData.latitude, formData.longitude]);

  const initializeMap = () => {
    const container = mapRef.current;
    if (!container || !window.kakao || !window.kakao.maps) return;

    try {
      const options = {
        center: new window.kakao.maps.LatLng(35.0156, 126.7108),
        level: 5,
      };

      const map = new window.kakao.maps.Map(container, options);
      mapInstanceRef.current = map;

      // 클릭 이벤트 추가
      window.kakao.maps.event.addListener(map, "click", (mouseEvent) => {
        const latlng = mouseEvent.latLng;

        // 기존 마커 제거
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }

        // 새 마커 생성
        const marker = new window.kakao.maps.Marker({
          position: latlng,
          map: map,
        });

        markerRef.current = marker;

        // 좌표 업데이트
        setFormData((prev) => ({
          ...prev,
          latitude: latlng.getLat().toFixed(6),
          longitude: latlng.getLng().toFixed(6),
        }));
      });
    } catch (error) {
      console.error("카카오맵 초기화 실패:", error);
    }
  };

  // 다음 주소 검색 스크립트 로드
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // 선택된 지역에 따른 기기 목록 업데이트
  useEffect(() => {
    if (selectedDistrict) {
      const district = najuDistricts.find((d) => d.name === selectedDistrict);
      if (district) {
        setSelectedDistrictCode(district.code);
      }
      loadDevicesByRegion(selectedDistrict);
    } else {
      setAvailableDevices([]);
      setSelectedDevices({
        sensor1: null,
        sensor2: null,
        sensor3: null,
        sensor4: null,
        sensor5: null,
      });
      setDevicePhotos({
        sensor1: [],
        sensor2: [],
        sensor3: [],
        sensor4: [],
        sensor5: [],
      });
      setSelectedDistrictCode("");
    }
  }, [selectedDistrict]);

  // 지역별 기기 목록 조회
  const loadDevicesByRegion = async (districtName) => {
    setIsLoadingDevices(true);
    try {
      const district = najuDistricts.find((d) => d.name === districtName);
      if (!district) {
        setAvailableDevices([]);
        return;
      }

      const response = await getDevicesByRegionApi(district.code);
      console.log("기기 목록 응답:", response);

      const devices = Array.isArray(response) ? response : response.content || [];

      // 먼저 기본 기기 목록을 설정 (할당 상태는 false로 초기화)
      const initialDevices = devices.map(device => ({
        ...device,
        isAssigned: false,
        assignedLocation: null
      }));
      
      setAvailableDevices(initialDevices);

      // 백그라운드에서 할당 상태 확인 후 업데이트
      Promise.all(
        devices.map(async (device) => {
          const assignedLocation = await checkDeviceAssignment(device.id);
          return {
            ...device,
            isAssigned: !!assignedLocation && assignedLocation.id !== parseInt(id),
            assignedLocation: assignedLocation
          };
        })
      ).then((devicesWithAssignmentStatus) => {
        setAvailableDevices(devicesWithAssignmentStatus);
      }).catch((error) => {
        console.error("기기 할당 상태 확인 실패:", error);
      });

    } catch (error) {
      console.error("기기 목록 조회 실패:", error);
      setAvailableDevices([]);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  // 기기 할당 상태 확인
  const checkDeviceAssignment = async (deviceId) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || "http://localhost:8090"}/api/locations/device/${deviceId}`);
      if (response.ok && response.status !== 204) {
        const data = await response.json();
        return data; // 할당된 소재지 정보 반환
      }
      return null; // 할당되지 않음
    } catch (error) {
      console.error("기기 할당 상태 확인 실패:", error);
      return null;
    }
  };

  // selectedDevice가 변경될 때 사진 업데이트 (탭별로 독립적)
  useEffect(() => {
    const currentSelectedDevice = selectedDevices[selectedTab];
    
    if (currentSelectedDevice) {
      console.log(`=== ${selectedTab} 선택된 기기 전체 데이터 ===`);
      console.log(currentSelectedDevice);
      console.log("=== 기기 사진 데이터 ===");
      console.log("photos 존재:", !!currentSelectedDevice.photos);
      console.log("photos 배열:", currentSelectedDevice.photos);

      if (currentSelectedDevice.photos && currentSelectedDevice.photos.length > 0) {
        const processedPhotos = currentSelectedDevice.photos.map((photo, index) => {
          console.log(`사진 ${index} 원본:`, photo);

          if (photo.fileUrl) {
            console.log(`사진 ${index} - fileUrl 사용:`, photo.fileUrl);
            return {
              ...photo,
              url: getDeviceImageUrl(photo.fileUrl),
            };
          }

          if (photo.url) {
            console.log(`사진 ${index} - url 사용:`, photo.url);
            return {
              ...photo,
              url: getDeviceImageUrl(photo.url),
            };
          }

          if (photo.filePath) {
            const fileName = photo.filePath.split("/").pop() || photo.filePath.split("\\").pop();
            const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8090";
            const finalUrl = `${API_BASE_URL}/api/files/device-photos/${fileName}`;
            console.log(`사진 ${index} - filePath 변환:`, finalUrl);
            return {
              ...photo,
              url: finalUrl,
            };
          }

          console.log(`사진 ${index} - 유효한 경로 없음`);
          return photo;
        });

        console.log("=== 처리된 사진 배열 ===");
        console.log(processedPhotos);
        setDevicePhotos(prev => ({
          ...prev,
          [selectedTab]: processedPhotos
        }));
      } else {
        console.log("사진 데이터가 없습니다.");
        setDevicePhotos(prev => ({
          ...prev,
          [selectedTab]: []
        }));
      }
    } else {
      setDevicePhotos(prev => ({
        ...prev,
        [selectedTab]: []
      }));
    }
  }, [selectedDevices, selectedTab]);

  // 주소 검색
  const searchAddress = () => {
    if (!window.daum || !window.daum.Postcode) {
      modal.alert("오류", "주소 검색 API가 로드되지 않았습니다.");
      return;
    }

    new window.daum.Postcode({
      oncomplete: function (data) {
        const roadAddr = data.roadAddress;
        const jibunAddr = data.jibunAddress;

        setFormData((prev) => ({
          ...prev,
          roadAddress: roadAddr,
          jibunAddress: jibunAddr,
          district: data.bname,
        }));

        // 주소로 좌표 검색
        const searchCoordinates = () => {
          if (
            window.kakao &&
            window.kakao.maps &&
            window.kakao.maps.services &&
            window.kakao.maps.services.Geocoder
          ) {
            const geocoder = new window.kakao.maps.services.Geocoder();
            geocoder.addressSearch(roadAddr, (result, status) => {
              if (status === window.kakao.maps.services.Status.OK && result[0]) {
                const coords = new window.kakao.maps.LatLng(result[0].y, result[0].x);

                // 지도 중심 이동
                if (mapInstanceRef.current) {
                  mapInstanceRef.current.setCenter(coords);

                  // 마커 표시
                  if (markerRef.current) {
                    markerRef.current.setMap(null);
                  }

                  const marker = new window.kakao.maps.Marker({
                    map: mapInstanceRef.current,
                    position: coords,
                  });

                  markerRef.current = marker;

                  // 좌표 업데이트
                  setFormData((prev) => ({
                    ...prev,
                    latitude: result[0].y,
                    longitude: result[0].x,
                  }));
                }
              }
            });
          } else {
            // services 라이브러리가 로드되지 않았다면 잠시 후 재시도
            setTimeout(searchCoordinates, 100);
          }
        };

        searchCoordinates();
      },
    }).open();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      const day = name.replace("day-", "");
      setFormData((prev) => ({
        ...prev,
        operationDays: {
          ...prev.operationDays,
          [day]: checked,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // 소재지명 변경 시 유효성 검사
    if (name === 'locationName') {
      if (!value.trim()) {
        console.warn('소재지명이 비어있습니다.');
      }
    }
  };

  const handleFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newPhotos: PhotoFile[] = [...photos]; // 타입 명시
        newPhotos[index] = {
          file,
          preview: reader.result as string,
          id: photos[index]?.id, // 옵셔널 체이닝 사용
        };
        setPhotos(newPhotos);
      };
      reader.readAsDataURL(file);
    }
  };

  // 사진 삭제 핸들러
  const handleRemovePhoto = async (index: number) => {
    const photo = photos[index];

    // 기존 사진 삭제 (수정 모드에서 서버에 저장된 사진인 경우)
    if (photo.id && isEditMode) {
      try {
        await deleteLocationPhotoApi(photo.id);
        console.log("사진 삭제 완료");
      } catch (error) {
        console.error("사진 삭제 실패:", error);
      }
    }

    // UI에서 제거
    const newPhotos: PhotoFile[] = [...photos];
    newPhotos[index] = { file: null, preview: "" };
    setPhotos(newPhotos);
  };

  const handleDeviceSelect = () => {
    const currentSelectedDevice = selectedDevices[selectedTab];
    if (currentSelectedDevice) {
      // 기기가 다른 소재지에 할당되어 있는 경우
      if (currentSelectedDevice.isAssigned) {
        modal.alert(
          "등록 불가",
          `해당 기기는 이미 "${currentSelectedDevice.assignedLocation.locationName}"에 할당되어 있습니다.\n\n기기를 등록하려면 먼저 기존 소재지에서 해제하거나 다른 기기를 선택해주세요.`
        );
        return;
      }

      setFormData((prev) => ({
        ...prev,
        sensors: {
          ...prev.sensors,
          [selectedTab]: {
            deviceId: currentSelectedDevice.id,
            deviceCode: currentSelectedDevice.deviceId,
            deviceName:
              currentSelectedDevice.installation?.deviceName ||
              currentSelectedDevice.deviceName ||
              currentSelectedDevice.brand ||
              "",
            latitude: currentSelectedDevice.installation?.latitude || currentSelectedDevice.latitude || "",
            longitude: currentSelectedDevice.installation?.longitude || currentSelectedDevice.longitude || "",
            locationDetail:
              currentSelectedDevice.installation?.locationDetail || currentSelectedDevice.locationDetail || "",
          },
        },
      }));

      modal.alert(
        "확인",
        `${selectedTab}에 ${currentSelectedDevice.installation?.deviceName || currentSelectedDevice.deviceName || currentSelectedDevice.deviceId} 등록완료`
      );
    }
  };

  const handleSubmit = async () => {
    try {
      // 필수 필드 검증
      if (!formData.locationName || !formData.locationName.trim()) {
        modal.alert("오류", "소재지명을 입력해주세요.");
        return;
      }
      if (!formData.locationType) {
        modal.alert("오류", "업종을 선택해주세요.");
        return;
      }
      if (!formData.latitude || !formData.longitude) {
        modal.alert("오류", "위치 정보를 설정해주세요.");
        return;
      }
      if (!formData.roadAddress && !formData.jibunAddress) {
        modal.alert("오류", "주소를 입력해주세요.");
        return;
      }

      // API 호출을 위한 데이터 준비
      const locationData = {
        locationCode: isEditMode
          ? formData.locationCode
          : `${formData.locationType ? formData.locationType.substring(0, 2) : 'XX'}${Date.now().toString().slice(-6)}`,
        locationName: formData.locationName,
        locationType: formData.locationType,
        businessNumber: formData.businessNumber,
        representative: formData.representative,
        contact: formData.contact,
        email: formData.contact2 && formData.contact2.includes("@") ? formData.contact2 : undefined,
        regionCode: selectedDistrictCode || undefined,
        province: formData.province,
        city: formData.city,
        district: formData.district,
        detailAddress: formData.detailAddress,
        roadAddress: formData.roadAddress,
        jibunAddress: formData.jibunAddress,
        latitude: formData.latitude,
        longitude: formData.longitude,
        establishmentDate: formData.establishmentDate,
        businessScale: formData.businessScale,
        employeeCount: formData.employeeCount ? parseInt(formData.employeeCount) : undefined,
        maxDeviceCount: 5,
        notes: formData.notes,
        odorInfo: formData.odorInfo,
        isPublic: formData.isPublic,
      };

      console.log(isEditMode ? "소재지 수정 데이터:" : "소재지 등록 데이터:", locationData);
      console.log("폼데이터 센서 정보:", formData.sensors);

      // 소재지 등록/수정 API 호출
      startLoading(isEditMode ? "소재지 정보 수정 중..." : "소재지 정보 등록 중...");

      let response;
      if (isEditMode) {
        response = await updateLocationApi(parseInt(id), locationData);
      } else {
        response = await createLocationApi(locationData);
      }

      console.log(isEditMode ? "소재지 수정 성공:" : "소재지 등록 성공:", response);

      // 사진 업로드 (새로운 파일만)
      const uploadPromises = [];
      for (let i = 0; i < photos.length; i++) {
        if (photos[i].file) {
          const formData = new FormData();
          formData.append("file", photos[i].file);
          formData.append("locationId", response.id.toString());
          formData.append("photoType", i === 0 ? "외부전경" : i === 1 ? "내부전경" : "주변환경");

          uploadPromises.push(uploadLocationPhotoApi(formData));
        }
      }

      if (uploadPromises.length > 0) {
        try {
          await Promise.all(uploadPromises);
          console.log("사진 업로드 완료");
        } catch (photoError) {
          console.error("사진 업로드 실패:", photoError);
        }
      }

      // 기기 연결 처리 (등록/수정 모두)
      const devicePromises = [];

      // 수정 모드에서 해제된 기기 삭제 처리
      if (isEditMode && originalDevices.length > 0) {
        const removePromises = [];
        
        for (const originalDevice of originalDevices) {
          const sensorNumber = originalDevice.sensorNumber;
          const sensorKey = `sensor${sensorNumber}`;
          const currentSensor = formData.sensors[sensorKey];
          
          // 현재 폼에서 해당 센서에 기기가 없거나 다른 기기로 변경된 경우
          if (!currentSensor?.deviceId || String(currentSensor.deviceId) !== String(originalDevice.deviceId)) {
            console.log(`센서 ${sensorNumber}에서 기기 ${originalDevice.deviceId} 해제 처리`);
            console.log(`비교: currentSensor.deviceId=${currentSensor?.deviceId}, originalDevice.deviceId=${originalDevice.deviceId}`);
            
            removePromises.push(
              removeDeviceFromLocationApi(response.id, originalDevice.deviceId)
                .then(() => {
                  console.log(`센서 ${sensorNumber} 기기 해제 성공`);
                })
                .catch((err) => {
                  console.error(`센서 ${sensorNumber} 기기 해제 실패:`, err);
                })
            );
          }
        }
        
        if (removePromises.length > 0) {
          try {
            await Promise.all(removePromises);
            console.log("기기 해제 처리 완료");
          } catch (error) {
            console.error("기기 해제 처리 중 오류:", error);
          }
        }
      }

      // 현재 폼에 있는 센서 정보 처리
      console.log("=== 기기 연결 처리 시작 ===");
      console.log("formData.sensors:", formData.sensors);
      
      for (const [sensorKey, sensor] of Object.entries(formData.sensors)) {
        console.log(`처리 중인 센서: ${sensorKey}`, sensor);
        if (sensor.deviceId) {
          const sensorNumber = parseInt(sensorKey.replace("sensor", ""));

          // 수정 모드에서는 기존 연결 확인
          if (isEditMode && originalDevices.length > 0) {
            const existingDevice = originalDevices.find(
              (d) => d.sensorNumber === sensorNumber && String(d.deviceId) === String(sensor.deviceId)
            );

            if (existingDevice) {
              console.log(`센서 ${sensorNumber}는 이미 연결됨`);
              continue;
            }
          }

          const addDeviceData = {
            deviceId: sensor.deviceId,
            sensorNumber: sensorNumber,
            sensorAlias: sensor.deviceName,
            installationDate: new Date().toISOString().split("T")[0],
            isPrimary: sensorNumber === 1,
            status: "ACTIVE",
          };

          console.log(`센서 ${sensorNumber} 연결 시도:`, addDeviceData);

          devicePromises.push(
            addDeviceToLocationApi(response.id, addDeviceData)
              .then((result) => {
                console.log(`센서 ${sensorNumber} 연결 성공:`, result);
                return result;
              })
              .catch((err) => {
                console.error(`센서 ${sensorNumber} 연결 실패:`, err);
                if (err.response) {
                  console.error("에러 응답:", err.response.data);
                }
                return null;
              })
          );
        }
      }

      if (devicePromises.length > 0) {
        console.log(`=== ${devicePromises.length}개 기기 연결 대기 중 ===`);
        const results = await Promise.allSettled(devicePromises);
        console.log("기기 연결 결과:", results);

        const failed = results.filter((r) => r.status === "rejected");
        const fulfilled = results.filter((r) => r.status === "fulfilled");
        
        console.log(`성공: ${fulfilled.length}개, 실패: ${failed.length}개`);
        
        if (failed.length > 0) {
          console.error("일부 기기 연결 실패:", failed);
          failed.forEach((result, index) => {
            if (result.status === "rejected") {
              console.error(`실패 상세 ${index}:`, result.reason);
            }
          });
          modal.alert("경고", "일부 기기 연결에 실패했습니다. 소재지는 저장되었습니다.");
        } else {
          console.log("모든 기기 연결 성공");
        }
      } else {
        console.log("=== 연결할 기기가 없음 ===");
      }

      stopLoading();
      modal.alert(
        "확인",
        isEditMode
          ? "소재지 정보가 성공적으로 수정되었습니다."
          : "소재지 정보가 성공적으로 등록되었습니다."
      );

      // 수정 모드에서는 상세 정보를 다시 로드하여 최신 데이터 반영
      setTimeout(() => {
        window.location.href = "/location";
      }, 1000);
    } catch (error) {
      console.error(isEditMode ? "수정 실패" : "등록 실패", error);
      stopLoading();
      modal.alert(
        "오류",
        error.message ||
          (isEditMode ? "수정 중 오류가 발생했습니다." : "등록 중 오류가 발생했습니다.")
      );
    }
  };

  // 이미지 URL 가져오기
  const getImageUrl = (path) => {
    if (!path) return "";

    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8090";
    const fileName = path.split("/").pop() || path.split("\\").pop();
    return `${API_BASE_URL}/api/files/location-photos/${fileName}`;
  };

  const getDeviceImageUrl = (path) => {
    if (!path) return "";

    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path;
    }

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8090";
    const fileName = path.split("/").pop() || path.split("\\").pop();
    return `${API_BASE_URL}/api/files/device-photos/${fileName}`;
  };

  const getDevicePhotosToDisplay = () => {
    const currentTabPhotos = devicePhotos[selectedTab];
    if (currentTabPhotos && currentTabPhotos.length > 0) {
      return currentTabPhotos;
    }

    const currentSensor = formData.sensors[selectedTab];
    if (currentSensor.photos && currentSensor.photos.length > 0) {
      return currentSensor.photos.map((photo, index) => {
        console.log(`현재 센서 사진 ${index}:`, photo);

        if (photo.fileUrl || photo.url) {
          return {
            ...photo,
            url: getDeviceImageUrl(photo.fileUrl) || getDeviceImageUrl(photo.url),
          };
        }
        if (photo.filePath) {
          const fileName = photo.filePath.split("/").pop() || photo.filePath.split("\\").pop();
          const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8090";
          return {
            ...photo,
            url: `${API_BASE_URL}/api/files/device-photos/${fileName}`,
          };
        }

        return photo;
      });
    }

    return [];
  };

  // 이미지 팝업 함수들
  const openImagePopup = (src: string, alt: string) => {
    setImagePopup({
      isOpen: true,
      src,
      alt,
    });
  };

  const closeImagePopup = () => {
    setImagePopup({
      isOpen: false,
      src: "",
      alt: "",
    });
  };

  // 기기 정보 표시 함수
  const renderDeviceInfo = () => {
    const currentSelectedDevice = selectedDevices[selectedTab];
    if (!currentSelectedDevice) return null;

    console.log("renderDeviceInfo - selectedDevice:", currentSelectedDevice);

    const hasInstallation =
      currentSelectedDevice.installation &&
      typeof currentSelectedDevice.installation === "object" &&
      Object.keys(currentSelectedDevice.installation).length > 0;

    const deviceInfo = hasInstallation ? currentSelectedDevice.installation : currentSelectedDevice;

    let regionInfo = "-";
    if (hasInstallation && deviceInfo.regionDistrict) {
      regionInfo = deviceInfo.regionDistrict;
    } else if (deviceInfo.regionProvince && deviceInfo.regionCity) {
      regionInfo = `${deviceInfo.regionProvince} ${deviceInfo.regionCity}`.trim();
      if (deviceInfo.regionDistrict) {
        regionInfo += ` ${deviceInfo.regionDistrict}`;
      }
    } else if (currentSelectedDevice.regionFullName) {
      regionInfo = currentSelectedDevice.regionFullName;
    }

    const deviceName =
      deviceInfo.deviceName || currentSelectedDevice.deviceName || currentSelectedDevice.brand || "-";

    const latitude = deviceInfo.latitude
      ? typeof deviceInfo.latitude === "object"
        ? deviceInfo.latitude.toString()
        : deviceInfo.latitude
      : currentSelectedDevice.latitude || "-";
    const longitude = deviceInfo.longitude
      ? typeof deviceInfo.longitude === "object"
        ? deviceInfo.longitude.toString()
        : deviceInfo.longitude
      : currentSelectedDevice.longitude || "-";

    const locationDetail =
      deviceInfo.locationDetail ||
      deviceInfo.inspectionNotes ||
      currentSelectedDevice.otherInfo ||
      "상세 위치 정보 없음";

    return (
      <div className="device-info-box">
        {/* 할당 상태 표시 */}
        {currentSelectedDevice.isAssigned && (
          <div className="device-assignment-warning" style={{
            backgroundColor: "#ffebee",
            border: "1px solid #f44336",
            borderRadius: "4px",
            padding: "10px",
            marginBottom: "15px",
            color: "#d32f2f"
          }}>
            <strong>⚠️ 이미 할당된 기기</strong>
            <p style={{ margin: "5px 0 0 0", fontSize: "14px" }}>
              현재 "{currentSelectedDevice.assignedLocation?.locationName}"에 할당되어 있습니다.
            </p>
          </div>
        )}

        <div className="device-info-item">
          <span className="device-info-label">아이디</span>
          <p className="device-info-value">{currentSelectedDevice.deviceId || "-"}</p>
        </div>

        <div className="device-info-item">
          <span className="device-info-label">구역</span>
          <p className="device-info-value">{regionInfo}</p>
        </div>

        <div className="device-info-item">
          <span className="device-info-label">기기명</span>
          <p className="device-info-value">{deviceName}</p>
        </div>

        <div className="device-info-item">
          <span className="device-info-label">위도/경도</span>
          <p className="device-info-value">
            {latitude} / {longitude}
          </p>
        </div>

        <div className="device-info-item">
          <span className="device-info-label">상세위치정보</span>
          <p className="device-info-value">{locationDetail}</p>
        </div>

        {/* 할당 상태 표시 */}
        <div className="device-info-item">
          <span className="device-info-label">할당상태</span>
          <p className="device-info-value" style={{ 
            color: currentSelectedDevice.isAssigned ? "#d32f2f" : "#2e7d32",
            fontWeight: "bold"
          }}>
            {currentSelectedDevice.isAssigned 
              ? `할당됨 (${currentSelectedDevice.assignedLocation?.locationName})`
              : "할당 가능"
            }
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="location-register-container">
      <div className="location-form loc_form01">
        <div className="loc_tdv01">
          <h2 className="section-title">소재지정보</h2>
          <div className="loc_rdv01">
            <label
              className="radio-label"
              style={{ display: "flex", alignItems: "center", gap: "5px" }}
            >
              <input
                type="radio"
                name="isPublic"
                value="true"
                checked={formData.isPublic === true}
                onChange={() => setFormData((prev) => ({ ...prev, isPublic: true }))}
              />
              <span>
                전체공개<span>(통합관제 표기 / 일반농가 표기)</span>
              </span>
            </label>
            <label
              className="radio-label"
              style={{ display: "flex", alignItems: "center", gap: "5px" }}
            >
              <input
                type="radio"
                name="isPublic"
                value="false"
                checked={formData.isPublic === false}
                onChange={() => setFormData((prev) => ({ ...prev, isPublic: false }))}
              />
              <span className="w2">
                비공개<span>(통합관제 표기, 일반농가 미표기 사업장주만 정보확인가능)</span>
              </span>
            </label>
          </div>
        </div>
        <div className="form-grid">
          {/* 좌측: 소재지정보 */}
          <div className="form-section">
            <div className="dvc_dv01">
              <table>
                <tbody>
                  <tr>
                    <th>
                      <label htmlFor="locationName">
                        소재지명 <span className="required">*</span>
                      </label>
                    </th>
                    <td>
                      <input
                        type="text"
                        id="locationName"
                        name="locationName"
                        value={formData.locationName}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="소재지명을 입력하세요"
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <label htmlFor="regionSelect">
                        소재지위치설정 <span className="required">*</span>
                      </label>
                    </th>
                    <td>
                      <div className="form-row" style={{ padding: "0" }}>
                        <div className="form-group">
                          <input type="text" className="form-select" disabled value="전라남도" />
                        </div>
                        <div className="form-group">
                          <input type="text" className="form-select" disabled value="나주시" />
                        </div>
                        <div className="form-group">
                          <select
                            name="regionDistrict"
                            value={formData.district}
                            onChange={(e) => {
                              const districtName = e.target.value;
                              setFormData((prev) => ({
                                ...prev,
                                district: districtName,
                              }));

                              // 선택한 지역의 코드 찾기
                              const region = najuDistricts.find((d) => d.name === districtName);
                              if (region) {
                                setSelectedDistrictCode(region.code);
                              } else {
                                setSelectedDistrictCode("");
                              }
                            }}
                            className="form-select"
                          >
                            <option value="">읍/면/동 선택</option>
                            {najuDistricts.map((district) => (
                              <option key={district.code} value={district.name}>
                                {district.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <label htmlFor="roadAddress">
                        소재지주소 <span className="required">*</span>
                      </label>
                    </th>
                    <td>
                      <div style={{ display: "flex", gap: "5px", marginBottom: "10px" }}>
                        <input
                          type="text"
                          value={formData.province}
                          readOnly
                          className="form-input"
                          style={{ flex: 1 }}
                        />
                        <button type="button" onClick={searchAddress} className="btn-check">
                          주소검색
                        </button>
                      </div>
                      <input
                        type="text"
                        name="roadAddress"
                        value={formData.roadAddress}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="도로명주소"
                        style={{ marginBottom: "10px" }}
                      />
                      <input
                        type="text"
                        name="jibunAddress"
                        value={formData.jibunAddress}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="지번주소"
                        style={{ marginBottom: "10px" }}
                      />
                      <input
                        type="text"
                        name="detailAddress"
                        value={formData.detailAddress}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="상세주소"
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <label htmlFor="representative">대표</label>
                    </th>
                    <td>
                      <input
                        type="text"
                        id="representative"
                        name="representative"
                        value={formData.representative}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="대표자명을 입력하세요"
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <label htmlFor="manager">담당자</label>
                    </th>
                    <td>
                      <input
                        type="text"
                        id="manager"
                        name="manager"
                        value={formData.manager}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="담당자명을 입력하세요"
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <label htmlFor="contact">연락처1</label>
                    </th>
                    <td>
                      <input
                        type="text"
                        id="contact"
                        name="contact"
                        value={formData.contact}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="010-0000-0000"
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <label htmlFor="contact2">연락처2</label>
                    </th>
                    <td>
                      <input
                        type="text"
                        id="contact2"
                        name="contact2"
                        value={formData.contact2}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="이메일 또는 추가 연락처"
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <label>조업일</label>
                    </th>
                    <td>
                      <div className="checkbox-group">
                        {[
                          { key: "mon", label: "월" },
                          { key: "tue", label: "화" },
                          { key: "wed", label: "수" },
                          { key: "thu", label: "목" },
                          { key: "fri", label: "금" },
                          { key: "sat", label: "토" },
                          { key: "sun", label: "일" },
                        ].map((day) => (
                          <label key={day.key} className="checkbox-label">
                            <input
                              type="checkbox"
                              name={`day-${day.key}`}
                              checked={formData.operationDays[day.key]}
                              onChange={handleChange}
                            />
                            <span
                              className={
                                day.key === "sat"
                                  ? "text-blue"
                                  : day.key === "sun"
                                    ? "text-red"
                                    : ""
                              }
                            >
                              {day.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <label htmlFor="operationHours">조업시간</label>
                    </th>
                    <td>
                      <input
                        type="text"
                        id="operationHours"
                        name="operationHours"
                        value={formData.operationHours}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="09:00 ~ 18:00"
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <label htmlFor="locationType">업종</label>
                    </th>
                    <td>
                      <select
                        id="locationType"
                        name="locationType"
                        value={formData.locationType}
                        onChange={handleChange}
                        className="form-input"
                        required
                      >
                        <option value="">업종을 선택하세요</option>
                        <option value="양돈">양돈</option>
                        <option value="양계">양계</option>
                        <option value="한우">한우</option>
                        <option value="젖소">젖소</option>
                        <option value="염소">염소</option>
                        <option value="기타축산">기타축산</option>
                        <option value="퇴비장">퇴비장</option>
                        <option value="음식물처리장">음식물처리장</option>
                        <option value="기타">기타</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <label htmlFor="odorInfo">악취정보</label>
                    </th>
                    <td>
                      <textarea
                        id="odorInfo"
                        name="odorInfo"
                        value={formData.odorInfo}
                        onChange={handleChange}
                        className="form-textarea"
                        rows={3}
                        placeholder="악취 관련 정보를 입력하세요"
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>
                      <label htmlFor="notes">기타특이사항</label>
                    </th>
                    <td>
                      <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        className="form-textarea"
                        rows={3}
                        placeholder="기타 특이사항을 입력하세요"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 중앙: 위치정보 및 사진 */}
          <div className="form-section2">
            <div className="dvc_wrap01">
              <div className="dvc_wrt01">
                <div className="subsection">
                  <h3 className="subsection-title">
                    소재지 위치정보 <span>(악취모니터링 지도에 표기함)</span>
                  </h3>
                  <div className="form-row2">
                    <div className="form-row-in">
                      <div className="form-group">
                        <label htmlFor="latitude">위도</label>
                        <input
                          type="text"
                          id="latitude"
                          name="latitude"
                          value={formData.latitude}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="위도 입력"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="longitude">경도</label>
                        <input
                          type="text"
                          id="longitude"
                          name="longitude"
                          value={formData.longitude}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="경도 입력"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="map-container">
                    <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
                  </div>
                </div>

                {/* 사진 업로드 */}
                <div className="photo-upload-section">
                  <h3 className="subsection-title">
                    사업장 사진 (휴대폰 <strong>세로비율</strong> 3:4)-클릭시 확대창 보입니다.
                  </h3>
                  <div className="photo-upload-grid">
                    {photos.map((photo, index) => (
                      <div key={index} className="photo-upload-box">
                        {photo.preview ? (
                          <>
                            <img
                              src={photo.preview}
                              alt={`사진 ${index + 1}`}
                              onClick={() => openImagePopup(photo.preview, `사업장 사진 ${index + 1}`)}
                              style={{ 
                                cursor: 'pointer',
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                backgroundColor: '#f0f0f0'
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemovePhoto(index)}
                              className="btn-remove-photo"
                            >
                              ×
                            </button>
                          </>
                        ) : (
                          <div className="photo-placeholder">
                            <span>이미지</span>
                          </div>
                        )}
                        <label htmlFor={`photo-${index}`} className="btn-file-select">
                          파일선택
                        </label>
                        <input
                          type="file"
                          id={`photo-${index}`}
                          accept="image/*"
                          onChange={(e) => handleFileChange(index, e)}
                          style={{ display: "none" }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="dvc_wrt02">
                {/* 센서 탭 */}
                <div className="sensor-tabs">
                  {["sensor1", "sensor2", "sensor3", "sensor4", "sensor5"].map((sensor) => (
                    <button
                      key={sensor}
                      type="button"
                      onClick={() => setSelectedTab(sensor)}
                      className={`sensor-tab ${selectedTab === sensor ? "active" : ""}`}
                    >
                      {sensor}
                    </button>
                  ))}
                </div>

                {/* 기기 조회 */}
                <div className="subsection">
                  <h3 className="subsection-title">기기조회</h3>
                  <div className="device-search-box">
                    <select
                      value={selectedDistrict}
                      onChange={(e) => setSelectedDistrict(e.target.value)}
                      className="form-select"
                      style={{ marginBottom: "10px" }}
                    >
                      <option value="">지역 선택</option>
                      {najuDistricts.map((district) => (
                        <option key={district.code} value={district.name}>
                          {district.name}
                        </option>
                      ))}
                    </select>

                    <div style={{ display: "flex", gap: "5px" }}>
                      <select
                        value={selectedDevices[selectedTab]?.id || ""}
                        onChange={(e) => {
                          const deviceId = parseInt(e.target.value);
                          const device = availableDevices.find(
                            (d) => d.id === deviceId
                          );
                          
                          // 이미 선택된 기기와 같은 경우 상태 업데이트 방지
                          const currentDevice = selectedDevices[selectedTab];
                          if (currentDevice?.id === deviceId) {
                            return;
                          }
                          
                          setSelectedDevices(prev => ({
                            ...prev,
                            [selectedTab]: device || null
                          }));
                        }}
                        className="form-select"
                        disabled={!selectedDistrict || isLoadingDevices}
                        style={{ flex: 1 }}
                      >
                        <option value="">{isLoadingDevices ? "조회중..." : "기기명"}</option>
                        {availableDevices.map((device) => (
                          <option 
                            key={device.id} 
                            value={device.id}
                            disabled={device.isAssigned}
                            style={{
                              color: device.isAssigned ? "#999" : "inherit",
                              backgroundColor: device.isAssigned ? "#f5f5f5" : "inherit"
                            }}
                          >
                            {device.installation?.deviceName ||
                              device.deviceName ||
                              device.brand ||
                              device.deviceId}
                            {device.isAssigned ? ` (이미 할당됨 - ${device.assignedLocation?.locationName})` : ""}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        onClick={handleDeviceSelect}
                        disabled={!selectedDevices[selectedTab] || selectedDevices[selectedTab]?.isAssigned}
                        className="btn-check"
                        style={{ 
                          opacity: (!selectedDevices[selectedTab] || selectedDevices[selectedTab]?.isAssigned) ? 0.5 : 1,
                          backgroundColor: selectedDevices[selectedTab]?.isAssigned ? "#f44336" : "#3498db",
                          cursor: selectedDevices[selectedTab]?.isAssigned ? "not-allowed" : "pointer"
                        }}
                        title={selectedDevices[selectedTab]?.isAssigned 
                          ? `이미 ${selectedDevices[selectedTab]?.assignedLocation?.locationName}에 할당된 기기입니다`
                          : "기기를 등록합니다"
                        }
                      >
                        {selectedDevices[selectedTab]?.isAssigned ? "할당불가" : "확인"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* 기기 정보 표시 */}
                {selectedDevices[selectedTab] && renderDeviceInfo()}

                {/* 현재 등록된 기기 */}
                {formData.sensors[selectedTab].deviceId && (
                  <div className="registered-device-box">
                    <h4>현재 등록된 기기</h4>
                    <p>
                      <strong>ID:</strong> {formData.sensors[selectedTab].deviceCode}
                    </p>
                    <p>
                      <strong>기기명:</strong> {formData.sensors[selectedTab].deviceName}
                    </p>
                    {formData.sensors[selectedTab].latitude && (
                      <p>
                        <strong>좌표:</strong> {formData.sensors[selectedTab].latitude},{" "}
                        {formData.sensors[selectedTab].longitude}
                      </p>
                    )}
                    {formData.sensors[selectedTab].locationDetail && (
                      <p>
                        <strong>위치:</strong> {formData.sensors[selectedTab].locationDetail}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          sensors: {
                            ...prev.sensors,
                            [selectedTab]: {
                              deviceId: null,
                              deviceCode: "",
                              deviceName: "",
                              latitude: "",
                              longitude: "",
                              locationDetail: "",
                            },
                          },
                        }));
                      }}
                      className="btn-disconnect"
                    >
                      연결 해제
                    </button>
                  </div>
                )}

                {/* 기기 사진 */}
                <div className="device-photos-grid">
                  {[0, 1, 2, 3].map((index) => {
                    const photosToDisplay = getDevicePhotosToDisplay();
                    const photo = photosToDisplay[index];

                    return (
                      <div key={index} className="device-photo-box">
                        {photo ? (
                          <img
                            src={photo.url || "https://via.placeholder.com/150?text=No+Image"}
                            alt={`기기 사진 ${index + 1}`}
                            onClick={() => openImagePopup(photo.url || "https://via.placeholder.com/150?text=No+Image", `센서 사진 ${index + 1}`)}
                            onError={(e) => {
                              // Error handling
                            }}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              backgroundColor: '#f0f0f0',
                              cursor: 'pointer'
                            }}
                          />
                        ) : (
                          <div className="device-photo-placeholder">
                            <span>이미지</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 버튼 영역 */}
        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={() => (window.location.href = "/location")}
          >
            취소
          </button>
          <button type="button" className="btn-submit" onClick={handleSubmit}>
            {isEditMode ? "수정하기" : "등록하기"}
          </button>
        </div>
      </div>

      {/* 이미지 팝업 모달 */}
      {imagePopup.isOpen && (
        <div className="image-popup-overlay" onClick={closeImagePopup}>
          <div className="image-popup-container" onClick={(e) => e.stopPropagation()}>
            <button className="image-popup-close" onClick={closeImagePopup}>
              ×
            </button>
            <img 
              src={imagePopup.src} 
              alt={imagePopup.alt} 
              className="image-popup-img"
            />
            <div className="image-popup-title">{imagePopup.alt}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationFormView;
